import { z } from 'zod';
import { HANDOVER_TYPES } from './handover.js';
// SCHEDULE: handover appointments and the staff assigned to them. Scheduling an appointment is
// separate from assigning staff; an assignment never grants a permission.
export const APPOINTMENT_STATUSES = ['planned', 'confirmed', 'completed', 'cancelled'];
export const APPOINTMENT_STATUS_LABELS = {
    planned: 'מתוכנן',
    confirmed: 'מאושר',
    completed: 'בוצע',
    cancelled: 'בוטל',
};
/** Statuses that block another appointment and count for overlaps. */
export const ACTIVE_APPOINTMENT_STATUSES = ['planned', 'confirmed'];
export const ASSIGNMENT_ROLES = ['primary', 'participant'];
export const ASSIGNMENT_ROLE_LABELS = {
    primary: 'אחראי',
    participant: 'מלווה',
};
export const ASSIGNMENT_STATUSES = ['requested', 'assigned', 'rejected', 'cancelled'];
export const ASSIGNMENT_STATUS_LABELS = {
    requested: 'ממתין לאישור',
    assigned: 'משובץ',
    rejected: 'נדחה',
    cancelled: 'בוטל',
};
export const SCHEDULE_LIMITS = {
    minDuration: 15,
    maxDuration: 720,
    notes: 1000,
    reason: 300,
    /** The longest period one calendar request may cover (a month view plus its edges). */
    rangeDays: 45,
};
const note = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((value) => (value === '' ? null : value));
export const assignmentSchema = z
    .object({
    id: z.uuid(),
    employee: z.object({ id: z.uuid(), fullName: z.string() }).strict(),
    role: z.enum(ASSIGNMENT_ROLES),
    status: z.enum(ASSIGNMENT_STATUSES),
    requestedBy: z.string(),
    /** True when the employee asked for it (or assigned themselves). */
    selfRequested: z.boolean(),
    /** Assigned directly by a coordinator, without a request. */
    direct: z.boolean(),
    decidedBy: z.string().nullable(),
    decisionNote: z.string().nullable(),
    cancelledBy: z.string().nullable(),
    cancelReason: z.string().nullable(),
    createdAt: z.iso.datetime({ offset: true }),
})
    .strict();
export const appointmentSchema = z
    .object({
    id: z.uuid(),
    apartment: z
        .object({ id: z.uuid(), building: z.string(), floor: z.string(), number: z.string() })
        .strict(),
    type: z.enum(HANDOVER_TYPES),
    startsAt: z.iso.datetime({ offset: true }),
    durationMinutes: z.number().int(),
    status: z.enum(APPOINTMENT_STATUSES),
    notes: z.string().nullable(),
    cancelReason: z.string().nullable(),
    rowVersion: z.number().int().min(1),
    /** The apartment's open draft of this type, if any (to open it from the calendar). */
    handoverId: z.uuid().nullable(),
    assignments: z.array(assignmentSchema),
})
    .strict();
/** `GET /projects/:id/schedule?from=&to=`: appointments starting in [from, to). */
export const scheduleResponseSchema = z
    .object({
    project: z.object({ id: z.uuid(), code: z.string(), name: z.string() }).strict(),
    appointments: z.array(appointmentSchema),
    /** Apartments to schedule (only with manage). */
    apartments: z.array(z
        .object({ id: z.uuid(), building: z.string(), floor: z.string(), number: z.string() })
        .strict()),
    /** The caller as an employee (null: the account is not linked to an employee). */
    me: z.object({ employeeId: z.uuid() }).strict().nullable(),
    permissions: z
        .object({
        manage: z.boolean(),
        approve: z.boolean(),
        assign: z.boolean(),
        request: z.boolean(),
    })
        .strict(),
})
    .strict();
export const appointmentInputSchema = z
    .object({
    apartmentId: z.uuid(),
    type: z.enum(HANDOVER_TYPES),
    startsAt: z.iso.datetime({ offset: true }),
    durationMinutes: z
        .number()
        .int()
        .min(SCHEDULE_LIMITS.minDuration)
        .max(SCHEDULE_LIMITS.maxDuration),
    notes: note(SCHEDULE_LIMITS.notes).default(null),
})
    .strict();
/** Rescheduling and editing; cancellation has its own request. */
export const appointmentUpdateSchema = z
    .object({
    rowVersion: z.number().int().min(1),
    startsAt: z.iso.datetime({ offset: true }),
    durationMinutes: z
        .number()
        .int()
        .min(SCHEDULE_LIMITS.minDuration)
        .max(SCHEDULE_LIMITS.maxDuration),
    status: z.enum(['planned', 'confirmed', 'completed']),
    notes: note(SCHEDULE_LIMITS.notes),
})
    .strict();
export const cancelRequestSchema = z
    .object({
    rowVersion: z.number().int().min(1),
    reason: z.string().trim().min(1).max(SCHEDULE_LIMITS.reason),
})
    .strict();
export const assignRequestSchema = z
    .object({ employeeId: z.uuid(), role: z.enum(ASSIGNMENT_ROLES) })
    .strict();
export const selfRequestSchema = z.object({ role: z.enum(ASSIGNMENT_ROLES) }).strict();
export const decisionSchema = z
    .object({ note: note(SCHEDULE_LIMITS.reason).default(null) })
    .strict();
export const assignmentCancelSchema = z
    .object({ reason: note(SCHEDULE_LIMITS.reason).default(null) })
    .strict();
/** Why an employee cannot take an appointment now. */
export const INELIGIBLE_REASONS = [
    'inactive',
    'not_member',
    'no_permission',
    'no_account',
    'overlap',
    'overlap_other_project',
];
/** `GET .../appointments/:id/candidates`: project members and whether each can be assigned. */
export const candidatesResponseSchema = z
    .object({
    items: z.array(z
        .object({
        employee: z.object({ id: z.uuid(), fullName: z.string() }).strict(),
        responsibility: z.string(),
        /** Reasons the employee cannot be the responsible employee / a participant now. */
        primary: z.array(z.enum(INELIGIBLE_REASONS)),
        participant: z.array(z.enum(INELIGIBLE_REASONS)),
    })
        .strict()),
})
    .strict();
/** Hebrew explanations of conflict and eligibility codes (409 `conflict.code`). */
export const SCHEDULE_CONFLICT_MESSAGES = {
    duplicate: 'לדירה כבר יש מועד פעיל למסירה מסוג זה. ערכו את המועד הקיים או בטלו אותו קודם.',
    overlap: 'לעובד יש שיבוץ מאושר אחר באותו זמן.',
    overlap_other_project: 'לעובד יש שיבוץ מאושר באותו זמן בפרויקט אחר.',
    primary_taken: 'למסירה כבר יש עובד אחראי. ניתן לשבץ את העובד כמלווה, או להסיר קודם את האחראי.',
    already_open: 'לעובד כבר יש בקשה או שיבוץ פתוחים למסירה זו.',
    inactive: 'העובד אינו פעיל.',
    not_member: 'העובד אינו חבר בצוות הפרויקט.',
    no_permission: 'לעובד אין הרשאה לבצע מסירות בפרויקט זה.',
    no_account: 'לעובד אין חשבון כניסה פעיל, ולכן אינו יכול לבצע מסירות.',
    appointment_closed: 'המסירה בוטלה או הסתיימה, ולא ניתן לשבץ אליה.',
    not_pending: 'הבקשה כבר טופלה.',
    stale: 'המועד עודכן במקום אחר. טענו את היומן מחדש ונסו שוב.',
    not_employee: 'החשבון שלך אינו מקושר לעובד, ולכן אינך יכול לבקש שיבוץ.',
};
//# sourceMappingURL=schedule.js.map